/**
 * IndexedDBCache - Persistent cache layer using IndexedDB
 * 
 * Provides L2 persistent storage for page layouts that survives:
 * - Page refreshes
 * - Browser restarts
 * - Tab closes
 * 
 * Key strategy: documentId + contentHash + pageIndex
 * - documentId: Unique document identifier (survives content changes)
 * - contentHash: Hash of document content (invalidates on edit)
 * - pageIndex: Zero-based page number
 */

import type { PageLayout } from '../../../core/types/Typesetter';
import { CacheLogger } from '../../../../../utils/loggers/CacheLogger';

const DB_NAME = 'edgedocs-page-cache';
const DB_VERSION = 1;
const STORE_NAME = 'pages';
const METADATA_STORE = 'metadata';

// Maximum age for cached entries (7 days)
const MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000;
// Maximum entries per document
const MAX_PAGES_PER_DOC = 500;
// Maximum total entries
const MAX_TOTAL_ENTRIES = 2000;

/**
 * Stored page entry structure
 */
interface StoredPageEntry {
  /** Composite key: documentId|contentHash|pageIndex */
  id: string;
  /** Document identifier */
  documentId: string;
  /** Content hash (changes on edit) */
  contentHash: string;
  /** Page index */
  pageIndex: number;
  /** Serialized page layout */
  pageLayout: PageLayout;
  /** Creation timestamp */
  createdAt: number;
  /** Last access timestamp */
  lastAccessed: number;
}

/**
 * Document metadata for cache management
 */
interface DocumentMetadata {
  documentId: string;
  contentHash: string;
  totalPages: number;
  lastUpdated: number;
}

/**
 * Generate composite key for IndexedDB
 */
function generateIDBKey(documentId: string, contentHash: string, pageIndex: number): string {
  return `${documentId}|${contentHash}|${pageIndex}`;
}

/**
 * Parse composite key
 */
function parseIDBKey(key: string): { documentId: string; contentHash: string; pageIndex: number } | null {
  const parts = key.split('|');
  if (parts.length !== 3) return null;
  
  const pageIndex = parseInt(parts[2], 10);
  if (isNaN(pageIndex)) return null;
  
  return {
    documentId: parts[0],
    contentHash: parts[1],
    pageIndex
  };
}

export class IndexedDBCache {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private isAvailable: boolean = true;
  
  constructor() {
    // Check if IndexedDB is available
    if (typeof indexedDB === 'undefined') {
      console.warn('[IndexedDBCache] IndexedDB not available in this environment');
      this.isAvailable = false;
    }
  }
  
  /**
   * Initialize the database connection
   */
  async init(): Promise<void> {
    if (!this.isAvailable) return;
    if (this.db) return;
    if (this.initPromise) return this.initPromise;
    
    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => {
        console.error('[IndexedDBCache] Failed to open database:', request.error);
        this.isAvailable = false;
        resolve(); // Don't reject - graceful degradation
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        console.log('[IndexedDBCache] Database opened successfully');
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create pages store with indexes
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('documentId', 'documentId', { unique: false });
          store.createIndex('contentHash', 'contentHash', { unique: false });
          store.createIndex('documentHash', ['documentId', 'contentHash'], { unique: false });
          store.createIndex('lastAccessed', 'lastAccessed', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
        }
        
        // Create metadata store
        if (!db.objectStoreNames.contains(METADATA_STORE)) {
          db.createObjectStore(METADATA_STORE, { keyPath: 'documentId' });
        }
        
        console.log('[IndexedDBCache] Database schema created/upgraded');
      };
    });
    
    return this.initPromise;
  }
  
  /**
   * Get a cached page
   */
  async get(
    documentId: string,
    contentHash: string,
    pageIndex: number
  ): Promise<PageLayout | null> {
    if (!this.isAvailable || !this.db) {
      await this.init();
      if (!this.db) return null;
    }
    
    return new Promise((resolve) => {
      const key = generateIDBKey(documentId, contentHash, pageIndex);
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.get(key);
      
      request.onsuccess = () => {
        const entry = request.result as StoredPageEntry | undefined;
        if (entry) {
          // Update last accessed time
          entry.lastAccessed = Date.now();
          store.put(entry);
          resolve(entry.pageLayout);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = () => {
        console.error('[IndexedDBCache] Get failed:', request.error);
        resolve(null);
      };
    });
  }
  
  /**
   * Get multiple pages at once (batch read)
   */
  async getPages(
    documentId: string,
    contentHash: string,
    pageIndices: number[]
  ): Promise<Map<number, PageLayout>> {
    if (!this.isAvailable || !this.db) {
      await this.init();
      if (!this.db) return new Map();
    }
    
    return new Promise((resolve) => {
      const results = new Map<number, PageLayout>();
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const now = Date.now();
      
      let pending = pageIndices.length;
      if (pending === 0) {
        resolve(results);
        return;
      }
      
      for (const pageIndex of pageIndices) {
        const key = generateIDBKey(documentId, contentHash, pageIndex);
        const request = store.get(key);
        
        request.onsuccess = () => {
          const entry = request.result as StoredPageEntry | undefined;
          if (entry) {
            entry.lastAccessed = now;
            store.put(entry);
            results.set(pageIndex, entry.pageLayout);
          }
          
          pending--;
          if (pending === 0) {
            resolve(results);
          }
        };
        
        request.onerror = () => {
          pending--;
          if (pending === 0) {
            resolve(results);
          }
        };
      }
    });
  }
  
  /**
   * Store a page in the cache
   */
  async set(
    documentId: string,
    contentHash: string,
    pageIndex: number,
    pageLayout: PageLayout
  ): Promise<void> {
    if (!this.isAvailable || !this.db) {
      await this.init();
      if (!this.db) return;
    }
    
    return new Promise((resolve) => {
      const key = generateIDBKey(documentId, contentHash, pageIndex);
      const now = Date.now();
      
      const entry: StoredPageEntry = {
        id: key,
        documentId,
        contentHash,
        pageIndex,
        pageLayout,
        createdAt: now,
        lastAccessed: now
      };
      
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.put(entry);
      
      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error('[IndexedDBCache] Set failed:', request.error);
        resolve();
      };
    });
  }
  
  /**
   * Store multiple pages at once (batch write)
   */
  async setPages(
    documentId: string,
    contentHash: string,
    pages: PageLayout[]
  ): Promise<void> {
    if (!this.isAvailable || !this.db || pages.length === 0) {
      await this.init();
      if (!this.db) return;
    }
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const now = Date.now();
      
      for (const page of pages) {
        const key = generateIDBKey(documentId, contentHash, page.page_index);
        const entry: StoredPageEntry = {
          id: key,
          documentId,
          contentHash,
          pageIndex: page.page_index,
          pageLayout: page,
          createdAt: now,
          lastAccessed: now
        };
        store.put(entry);
      }
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => {
        console.error('[IndexedDBCache] Batch set failed:', transaction.error);
        resolve();
      };
    });
  }
  
  /**
   * Invalidate all pages for a document with a specific content hash
   * (Called when document content changes)
   */
  async invalidateByHash(documentId: string, contentHash: string): Promise<number> {
    if (!this.isAvailable || !this.db) return 0;
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('documentHash');
      
      const range = IDBKeyRange.only([documentId, contentHash]);
      const request = index.openCursor(range);
      
      let deleted = 0;
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          deleted++;
          cursor.continue();
        }
      };
      
      transaction.oncomplete = () => {
        console.log(`[IndexedDBCache] Invalidated ${deleted} entries for ${documentId}/${contentHash}`);
        resolve(deleted);
      };
      
      transaction.onerror = () => resolve(deleted);
    });
  }
  
  /**
   * Invalidate all pages from a certain page index onward
   * (For future partial invalidation support)
   */
  async invalidateFromPage(
    documentId: string,
    contentHash: string,
    fromPageIndex: number
  ): Promise<number> {
    if (!this.isAvailable || !this.db) return 0;
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('documentHash');
      
      const range = IDBKeyRange.only([documentId, contentHash]);
      const request = index.openCursor(range);
      
      let deleted = 0;
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const entry = cursor.value as StoredPageEntry;
          if (entry.pageIndex >= fromPageIndex) {
            cursor.delete();
            deleted++;
          }
          cursor.continue();
        }
      };
      
      transaction.oncomplete = () => {
        console.log(`[IndexedDBCache] Invalidated ${deleted} entries from page ${fromPageIndex}`);
        resolve(deleted);
      };
      
      transaction.onerror = () => resolve(deleted);
    });
  }
  
  /**
   * Check if a specific document/hash combination exists in cache
   */
  async hasDocument(documentId: string, contentHash: string): Promise<boolean> {
    if (!this.isAvailable || !this.db) {
      await this.init();
      if (!this.db) return false;
    }
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('documentHash');
      
      const range = IDBKeyRange.only([documentId, contentHash]);
      const request = index.count(range);
      
      request.onsuccess = () => resolve(request.result > 0);
      request.onerror = () => resolve(false);
    });
  }
  
  /**
   * Get count of cached pages for a document/hash
   */
  async getPageCount(documentId: string, contentHash: string): Promise<number> {
    if (!this.isAvailable || !this.db) {
      await this.init();
      if (!this.db) return 0;
    }
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('documentHash');
      
      const range = IDBKeyRange.only([documentId, contentHash]);
      const request = index.count(range);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(0);
    });
  }
  
  /**
   * Clean up old entries (called periodically)
   */
  async cleanup(): Promise<{ expired: number; overflow: number }> {
    if (!this.isAvailable || !this.db) return { expired: 0, overflow: 0 };
    
    const cutoff = Date.now() - MAX_CACHE_AGE_MS;
    let expired = 0;
    let overflow = 0;
    
    // Remove expired entries
    await new Promise<void>((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('lastAccessed');
      
      const range = IDBKeyRange.upperBound(cutoff);
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          expired++;
          cursor.continue();
        }
      };
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
    });
    
    // Check total count and evict oldest if needed
    const totalCount = await this.getTotalCount();
    if (totalCount > MAX_TOTAL_ENTRIES) {
      const toRemove = totalCount - MAX_TOTAL_ENTRIES;
      overflow = await this.evictOldest(toRemove);
    }
    
    if (expired > 0 || overflow > 0) {
      CacheLogger.cleanup.removed('IndexedDB', expired, overflow);
    }
    
    return { expired, overflow };
  }
  
  /**
   * Get total entry count
   */
  private async getTotalCount(): Promise<number> {
    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count();
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(0);
    });
  }
  
  /**
   * Evict oldest entries
   */
  private async evictOldest(count: number): Promise<number> {
    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('lastAccessed');
      
      const request = index.openCursor();
      let removed = 0;
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor && removed < count) {
          cursor.delete();
          removed++;
          cursor.continue();
        }
      };
      
      transaction.oncomplete = () => resolve(removed);
      transaction.onerror = () => resolve(removed);
    });
  }
  
  /**
   * Get cache statistics
   */
  async getStats(): Promise<{
    totalEntries: number;
    uniqueDocuments: number;
    oldestEntry: number | null;
    newestEntry: number | null;
  }> {
    if (!this.isAvailable || !this.db) {
      return { totalEntries: 0, uniqueDocuments: 0, oldestEntry: null, newestEntry: null };
    }
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      const stats = {
        totalEntries: 0,
        uniqueDocuments: 0,
        oldestEntry: null as number | null,
        newestEntry: null as number | null
      };
      
      const documentIds = new Set<string>();
      
      const request = store.openCursor();
      
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const entry = cursor.value as StoredPageEntry;
          stats.totalEntries++;
          documentIds.add(entry.documentId);
          
          if (stats.oldestEntry === null || entry.createdAt < stats.oldestEntry) {
            stats.oldestEntry = entry.createdAt;
          }
          if (stats.newestEntry === null || entry.createdAt > stats.newestEntry) {
            stats.newestEntry = entry.createdAt;
          }
          
          cursor.continue();
        } else {
          stats.uniqueDocuments = documentIds.size;
          resolve(stats);
        }
      };
      
      request.onerror = () => resolve(stats);
    });
  }
  
  /**
   * Clear all cached data
   */
  async clear(): Promise<void> {
    if (!this.isAvailable || !this.db) return;
    
    return new Promise((resolve) => {
      const transaction = this.db!.transaction([STORE_NAME, METADATA_STORE], 'readwrite');
      transaction.objectStore(STORE_NAME).clear();
      transaction.objectStore(METADATA_STORE).clear();
      
      transaction.oncomplete = () => {
        console.log('[IndexedDBCache] Cache cleared');
        resolve();
      };
      transaction.onerror = () => resolve();
    });
  }
  
  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.initPromise = null;
    }
  }
}

// Singleton instance for app-wide use
let _instance: IndexedDBCache | null = null;

export function getIndexedDBCache(): IndexedDBCache {
  if (!_instance) {
    _instance = new IndexedDBCache();
  }
  return _instance;
}
